// M19 Events Foundation: shared, dependency-free rules for the "Start an event" flow (client app AND staff app).
// The database (migration 283) re-applies every rule here; this file only keeps the screens honest and previewable.
// No third-party imports: the client app builds this file with its own (smaller) dependency set.
import { isRealAddress } from '../../components/shared/addressRules';

export const SWITCHES = [
    { key: 'invitations', label: 'Send invitations', help: 'Staff and the client can send each invitee their invitation. Nothing goes out until someone presses send.' },
    { key: 'reminders', label: 'RSVP and travel-info reminders', help: 'Remind invitees who have not answered or sent flights.' },
    { key: 'self_service_flights', label: 'Invitees enter their own flights', help: 'Each invitee adds or updates their flights.' },
    { key: 'flight_tracking', label: 'Verify and track flights', help: 'Check each arriving flight and follow delays.' },
    { key: 'ground_transport', label: 'Group ground transport', help: 'Build shared airport and daily rides from the day plan.' },
    { key: 'auto_confirm_rides', label: 'Auto-confirm rides', help: 'Confirm a ride automatically when every check passes.' },
    { key: 'flight_change_handling', label: 'Handle flight changes', help: 'Flag rides when a flight changes so dispatch can adjust.' },
    { key: 'survey', label: 'Post-event survey', help: 'Ask invitees how it went.' },
];
export const SWITCH_KEYS = SWITCHES.map((s) => s.key);

export const PRESETS = [
    { key: 'full', label: 'Full', help: 'Automations run on their own. Rides confirm themselves when every check passes.' },
    { key: 'guided', label: 'Guided', help: 'Automations prepare everything; our team approves before anything is confirmed.' },
    { key: 'manual', label: 'Manual', help: 'Nothing runs automatically. Our team handles each step.' },
];

export const SECTIONS = [
    { key: 'flights', label: 'Flights' },
    { key: 'lodging', label: 'Lodging' },
    { key: 'ground_transport', label: 'Ground transport' },
    { key: 'meals', label: 'Meals and dietary' },
    { key: 'checkin', label: 'Check-in' },
];

export const TAGS = [
    { key: 'local', label: 'Local' },
    { key: 'driving', label: 'Driving' },
    { key: 'staff', label: 'Staff / employee' },
    { key: 'rep', label: 'Rep' },
    { key: 'cancelled', label: 'Cancelled' },
];

export const RIDERS = [
    { key: 'all', label: 'Everyone (including locals)' },
    { key: 'arrivals', label: 'Arriving flyers' },
    { key: 'departures', label: 'Departing flyers' },
    { key: 'locals', label: 'Locals only' },
    { key: 'custom', label: 'A set number' },
];

export const TIMINGS = [
    { key: 'fixed', label: 'At a set time' },
    { key: 'arrivals', label: 'Tied to arriving flights' },
    { key: 'departures', label: 'Tied to departing flights' },
];

export const STATUS_LABELS = { requested: 'Waiting for review', accepted: 'Accepted', declined: 'Declined', withdrawn: 'Withdrawn', canceled: 'Canceled' };

/** Switch values for a preset before anyone changes a switch (mirrors m19f_resolve). */
export function presetValues(preset) {
    return Object.fromEntries(SWITCH_KEYS.map((k) => [k, preset === 'manual' ? false : preset === 'guided' ? k !== 'auto_confirm_rides' : true]));
}

/**
 * What will actually be stored. A client only changes switches the admin shows AND lets them change; anything else takes the
 * admin default. Guided and Manual never auto-confirm.
 */
export function resolveSwitches(policySwitches = {}, preset = 'guided', requested = {}, forClient = true) {
    const base = presetValues(preset);
    return Object.fromEntries(SWITCH_KEYS.map((k) => {
        const rule = policySwitches[k];
        let value = base[k];
        if (forClient && !(rule && rule.editable)) value = Boolean(rule?.default);
        else if (typeof requested[k] === 'boolean') value = requested[k];
        if (preset !== 'full' && k === 'auto_confirm_rides') value = false;
        return [k, value];
    }));
}

export function eventDays(start, end) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(start || '')) return 0;
    const last = /^\d{4}-\d{2}-\d{2}$/.test(end || '') ? end : start;
    return Math.round((Date.parse(`${last}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86400000) + 1;
}

const has = (tags, tag) => Array.isArray(tags) && tags.includes(tag);

/** Counts from invitees (mirrors m19f_counts), or the entered counts for a head-count-only event. */
export function countInvitees(invitees = []) {
    const live = invitees.filter((i) => !has(i.tags, 'cancelled'));
    const flies = (leg) => Boolean(leg && (leg.flight_number || leg.time));
    return {
        invited: live.length,
        confirmed: live.filter((i) => i.rsvp_status === 'accepted').length,
        arrivals: live.filter((i) => flies(i.arrival) && !has(i.tags, 'local') && !has(i.tags, 'driving')).length,
        daily: live.filter((i) => !has(i.tags, 'driving')).length,
        returns: live.filter((i) => flies(i.departure) && !has(i.tags, 'local') && !has(i.tags, 'driving')).length,
        locals: live.filter((i) => has(i.tags, 'local')).length,
    };
}

export function moveCount(move, counts) {
    switch (move.riders) {
    case 'all': return counts.daily || 0;
    case 'arrivals': return counts.arrivals || 0;
    case 'departures': return counts.returns || 0;
    case 'locals': return counts.locals || 0;
    default: return Number(move.custom_count) || 0;
    }
}

/** US-first phone tidy without a library; anything else is sent as typed and the server keeps it in notes. */
export function tidyPhone(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const digits = raw.replace(/\D/g, '');
    if (raw.startsWith('+') && digits.length >= 8 && digits.length <= 15) return `+${digits}`;
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return raw;
}

/** Small CSV reader (quotes, commas, new lines). */
export function readCsv(text) {
    const rows = []; let row = [], cell = '', quoted = false;
    const source = String(text || '').replace(/^﻿/, '');
    for (let i = 0; i < source.length; i += 1) {
        const ch = source[i];
        if (quoted) {
            if (ch === '"' && source[i + 1] === '"') { cell += '"'; i += 1; } else if (ch === '"') quoted = false; else cell += ch;
        } else if (ch === '"') quoted = true;
        else if (ch === ',' || ch === '\t') { row.push(cell); cell = ''; }
        else if (ch === '\n' || ch === '\r') { if (ch === '\r' && source[i + 1] === '\n') i += 1; row.push(cell); rows.push(row); row = []; cell = ''; }
        else cell += ch;
    }
    if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
    return rows.filter((r) => r.some((c) => String(c).trim() !== ''));
}

const TAG_WORDS = /\b(LOCAL|DRIVING|CANCELL?ED|STAFF|EMPLOYEE|REP)\b/gi;
function tagsFrom(text) {
    const found = String(text || '').match(TAG_WORDS) || [];
    return [...new Set(found.map((t) => {
        const v = t.toLowerCase();
        if (v.startsWith('cancel')) return 'cancelled';
        if (v === 'employee') return 'staff';
        return v;
    }))];
}

function splitName(full) {
    const clean = String(full || '').replace(TAG_WORDS, '').replace(/[()[\]]/g, '').replace(/\s+/g, ' ').trim();
    const parts = clean.split(' ');
    return { first_name: parts.length > 1 ? parts.slice(0, -1).join(' ') : clean, last_name: parts.length > 1 ? parts[parts.length - 1] : '' };
}

const HEADER = { name: /^(full\s*(legal\s*)?name|name|guest\s*name)$/i, first_name: /^first/i, last_name: /^last/i, email: /e-?mail/i,
    phone: /phone|cell|mobile/i, company: /company/i, preferred_airport: /airport/i, tags: /tags?|type/i, dietary: /dietary|meal/i, notes: /notes?/i,
    arrival_flight: /arriv.*flight|flight.*arriv/i, arrival_date: /arriv.*date|date.*arriv/i, arrival_time: /arriv.*time/i,
    departure_flight: /depart.*flight|flight.*depart/i, departure_date: /depart.*date|date.*(leav|depart)/i, departure_time: /depart.*time/i };

/** Invitees from pasted lines ("Name, email, phone") or a CSV with a header row. */
export function parseInvitees(text) {
    const rows = readCsv(text);
    if (!rows.length) return [];
    const header = rows[0].map((h) => String(h).trim());
    const keyed = header.map((h) => Object.keys(HEADER).find((k) => HEADER[k].test(h)) || null);
    const hasHeader = keyed.filter(Boolean).length >= 2 && keyed.some((k) => k === 'name' || k === 'first_name');
    const body = hasHeader ? rows.slice(1) : rows;
    return body.map((cells) => {
        const r = hasHeader ? Object.fromEntries(keyed.map((k, i) => [k, String(cells[i] ?? '').trim()]).filter(([k]) => k))
            : { name: cells[0], email: (cells.find((c) => /@/.test(c)) || '').trim(), phone: (cells.slice(1).find((c) => /\d{7,}/.test(String(c).replace(/\D/g, ''))) || '').trim() };
        const named = r.first_name ? { first_name: r.first_name, last_name: r.last_name || '' } : splitName(r.name);
        return {
            ...named, email: (r.email || '').toLowerCase(), phone: tidyPhone(r.phone), company: r.company || '',
            preferred_airport: (r.preferred_airport || '').toUpperCase().slice(0, 4), dietary: r.dietary || '', notes: r.notes || '',
            tags: [...new Set([...tagsFrom(r.tags), ...tagsFrom(r.name)])],
            arrival: { flight_number: (r.arrival_flight || '').replace(/\s/g, '').toUpperCase(), flight_date: r.arrival_date || '', time: r.arrival_time || '' },
            departure: { flight_number: (r.departure_flight || '').replace(/\s/g, '').toUpperCase(), flight_date: r.departure_date || '', time: r.departure_time || '' },
        };
    }).filter((i) => i.first_name);
}

/** A row from the Group Transfers workbook parser -> an invitee (tags from the name markers it already reads). */
export function inviteeFromWorkbookRow(row) {
    const named = splitName(row.name || '');
    const tags = tagsFrom(`${row.original_name || ''} ${row.role || ''}`);
    if (row.excluded && !tags.includes('cancelled')) tags.push('cancelled');
    const depAirport = String(row.departure?.route || '').split('/')[0] || '';
    return {
        ...named, email: row.email || '', phone: row.phone || '', company: row.company || '', preferred_airport: (row.preferred_airport || '').toUpperCase(),
        notes: row.notes || '', tags,
        arrival: { airline: row.arrival?.airline || '', flight_number: row.arrival?.flight_number || '', flight_date: row.arrival?.flight_date || '', time: row.arrival?.arrival_time || '', airport: row.arrival?.arrival_airport || '' },
        departure: { airline: row.departure?.airline || '', flight_number: row.departure?.flight_number || '', flight_date: row.departure?.flight_date || '', time: row.departure?.departure_time || '', airport: /^[A-Z]{3,4}$/.test(depAirport) ? depAirport : '' },
    };
}

// ---- T4: times ("HH:MM" stored; typed "4:30 pm", "430pm", "1630", "16:30", "4" all work). Nothing here ever reads the clock.
/** Minutes since midnight, snapped to the step (default 5), or null when it cannot be read. */
export function parseClock(text, { step = 5 } = {}) {
    const raw = String(text || '').trim().toLowerCase().replace(/\./g, '');
    const m = raw.match(/^(\d{1,2})(?::?(\d{2}))?\s*([ap])?m?$/);
    if (!m) return null;
    let hours = Number(m[1]);
    const minutes = m[2] === undefined ? 0 : Number(m[2]);
    if (minutes > 59 || hours > 23) return null;
    if (m[3]) {
        if (hours < 1 || hours > 12) return null;
        hours = (hours % 12) + (m[3] === 'p' ? 12 : 0);
    }
    const total = hours * 60 + minutes;
    return (Math.round(total / step) * step) % 1440;
}
export const toClock = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
export function clockLabel(clock) {
    const [h, m] = String(clock || '').split(':').map(Number);
    if (!Number.isFinite(h)) return '';
    return `${h % 12 || 12}:${String(m || 0).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}
export const TIME_STEP_OPTIONS = Array.from({ length: 288 }, (_, i) => ({ value: toClock(i * 5), label: clockLabel(toClock(i * 5)) }));

/** T3: both required; the end strictly after the start. Returns plain problems. Never fills in a time. */
export function timeProblems({ start_date: startDate, end_date: endDate, start_time: startTime, end_time: endTime }) {
    const problems = [];
    if (!startTime) problems.push('Enter the start time.');
    if (!endTime) problems.push('Enter the end time.');
    if (startTime && endTime && startDate && `${endDate || startDate}T${endTime}` <= `${startDate}T${startTime}`) problems.push('The end must be after the start.');
    return problems;
}

export const TRANSPORT_MODES = [
    { key: 'client_arranged', label: 'Client-arranged', help: 'Guests see the transport provider’s name and “Contact the event coordinator”.' },
    { key: 'guest_arranged', label: 'Guest-arranged', help: 'Guests see a Request Transportation button that sends a normal request.' },
];

// P5: what guests can see, on unless someone turns it off. Each option is applied by the guest page's server function.
export const GUEST_VIEW_OPTIONS = [
    { key: 'schedule', label: 'Event schedule and shuttle times' },
    { key: 'venue', label: 'Venue name and address' },
    { key: 'hotel', label: 'Hotel details' },
    { key: 'provider', label: 'Transport provider name' },
    { key: 'coordinator', label: 'Event coordinator contact' },
    { key: 'pickup_times', label: 'Their own pickup times' },
    { key: 'plus_ones', label: 'Plus-ones section' },
    { key: 'request_transport', label: 'Request Transportation button' },
];
export const withGuestViewDefaults = (value) => Object.fromEntries(GUEST_VIEW_OPTIONS.map((o) => [o.key, value?.[o.key] !== false]));
// The plus-ones choice only matters when plus-ones are on; the button only exists for guest-arranged transport.
export const guestViewOptionsFor = ({ plus_ones_enabled: plus, guest_transport_mode: mode }) => GUEST_VIEW_OPTIONS.map((o) => o.key)
    .filter((key) => (key !== 'plus_ones' || plus) && (key !== 'request_transport' || mode === 'guest_arranged'));

// P6: the types on an approved list.
export const VENDOR_KINDS = [
    { key: 'hotel', label: 'Hotel' }, { key: 'venue', label: 'Venue' }, { key: 'transport_provider', label: 'Transport provider' }, { key: 'caterer', label: 'Caterer' }, { key: 'other', label: 'Other' },
];
export const vendorKindLabel = (key) => VENDOR_KINDS.find((k) => k.key === key)?.label || key;

export const EMPTY_EVENT = Object.freeze({ kit_key: '', event_type_other: '', name: '', start_date: '', end_date: '', start_time: '', end_time: '',
    plus_ones_enabled: false, plus_ones_max: '', guest_transport_mode: 'client_arranged', transport_provider_name: '', coordinator_name: '', coordinator_phone: '', coordinator_email: '', venue_name: '', venue_address: '', lodging_name: '', lodging_address: '',
    host_name: '', program_id: '', notes: '', invitees: [], headcount_only: false, counts: { invited: '', arrivals: '', daily: '', returns: '' }, day_plan: [],
    automation: { preset: '', switches: {} } });

/** Per-step checks: returns plain-English problems, empty when the step can continue. */
export function stepProblems(step, form, kit) {
    const problems = [];
    if (step === 'type' && !form.kit_key) problems.push('Choose an event type.');
    if (step === 'type' && form.kit_key === 'other' && !String(form.event_type_other || '').trim()) problems.push('Type a short name for this kind of event.');
    if (step === 'details') {
        if (!form.name.trim()) problems.push('Enter the event name.');
        if (!form.start_date) problems.push('Choose the first day.');
        const days = eventDays(form.start_date, form.end_date);
        if (form.start_date && (days < 1 || days > 60)) problems.push('The last day must be on or after the first day, within 60 days.');
        problems.push(...timeProblems(form));
        if (String(form.lodging_name || '').trim() && !isRealAddress(form.lodging_address)) problems.push('Enter the hotel’s full address: street, city, state and ZIP, or pick it from the search.');
        if (form.plus_ones_enabled && !(Number(form.plus_ones_max) >= 1 && Number(form.plus_ones_max) <= 10)) problems.push('Enter how many plus-ones each guest may bring, 1 to 10.');
    }
    if (step === 'invitees' && !form.headcount_only && !form.invitees.length) problems.push('Add invitees, or choose head count only.');
    if (step === 'invitees' && form.headcount_only && !(Number(form.counts.invited) > 0)) problems.push('Enter how many people are invited.');
    if (step === 'plan') {
        const days = eventDays(form.start_date, form.end_date);
        form.day_plan.forEach((m, i) => {
            if (!m.from_label || !m.to_label) problems.push(`Move ${i + 1}: enter where it starts and ends.`);
            if (m.day_number > days) problems.push(`Move ${i + 1}: the day is after the event ends.`);
        });
    }
    if (kit && step === 'details' && kit.sections?.lodging === false) return problems;
    return problems;
}

/** The request body for m19_client_start_event / m19_staff_start_event. */
export function eventPayload(form) {
    const counts = Object.fromEntries(Object.entries(form.counts || {}).filter(([, v]) => v !== '' && v != null).map(([k, v]) => [k, Number(v)]));
    return {
        kit_key: form.kit_key, event_type_other: form.kit_key === 'other' ? String(form.event_type_other || '').trim() : '', name: form.name.trim(), start_date: form.start_date, end_date: form.end_date || form.start_date,
        start_time: form.start_time, end_time: form.end_time,
        plus_ones_enabled: Boolean(form.plus_ones_enabled), plus_ones_max: form.plus_ones_enabled ? Number(form.plus_ones_max) : 0, guest_transport_mode: form.guest_transport_mode || 'client_arranged',
        transport_provider_name: form.transport_provider_name || '', coordinator_name: form.coordinator_name || '', coordinator_phone: form.coordinator_phone || '', coordinator_email: form.coordinator_email || '',
        venue_name: form.venue_name, venue_address: form.venue_address, lodging_name: form.lodging_name, lodging_address: form.lodging_address,
        host_name: form.host_name, program_id: form.program_id || null, notes: form.notes,
        headcount_only: Boolean(form.headcount_only), counts,
        invitees: form.headcount_only ? [] : form.invitees,
        day_plan: form.day_plan.map(({ key, ...m }) => ({ ...m, at_time: m.timing === 'fixed' ? m.at_time : '', custom_count: m.riders === 'custom' ? m.custom_count : '' })),
        automation: { preset: form.automation.preset, switches: form.automation.switches },
        guest_visibility: withGuestViewDefaults(form.guest_visibility),
        ...(form.venue_vendor_id || form.lodging_vendor_id || form.provider_vendor_id ? { places: {
            ...(form.venue_vendor_id ? { venue: { vendor_id: form.venue_vendor_id } } : {}),
            ...(form.lodging_vendor_id ? { hotels: [{ vendor_id: form.lodging_vendor_id }] } : {}),
            ...(form.provider_vendor_id ? { transport_provider: { vendor_id: form.provider_vendor_id } } : {}),
        } } : {}),
    };
}
